<template>
  <div id="client-events">
    <div v-if="loaded">
      <div style="position: absolute; margin-top: 3px; right: 160px">
        <b-spinner small v-if="loading">{{ $t('operations.loading') }}</b-spinner>
        <b-link v-else><i class="fa-solid fa-rotate-right" @click="loadData"></i></b-link>
      </div>
      <b-button variant="primary" size="sm" class="mb-1" style="float: right" @click="openModal()">
        {{ $t('operations.addNewEvent') }}
      </b-button>
      <h4 class="text-primary text-center text-uppercase">
        <i class="fa-solid fa-calendar"></i>
        {{ $t('tabs.clients.events') }}
      </h4>
      <b-table striped small hover :items="events"
               v-handy-scroll responsive
               :fields="fields"
      >

        <template #cell(actions)="data">
          <div class="d-flex justify-content-left">
            <!-- Edit Button -->
            <b-button
                size="sm"
                variant="warning"
                class="mx-1"
                @click="onEditEvent(data.item)"
            >
              <feather-icon icon="Edit3Icon" size="14" class="mr-50" />
              {{ $t('operations.edit') }}
            </b-button>

            <!-- Delete Button -->
            <b-button
                size="sm"
                variant="danger"
                class="mx-1"
                @click="onDeleteEvent(data.item.id)"
            >
              <feather-icon icon="Trash2Icon" size="14" class="mr-50" />
              {{ $t('operations.delete') }}
            </b-button>
          </div>
        </template>

        <template #cell(body)="data">
          <div>{{ data.value }}</div>
        </template>


        <template #cell(duration)="data">
          <span style="white-space: nowrap">{{formatDuration(data.value)}}</span>
        </template>

        <template #cell(type)="data">
          <b-badge :variant="$t(`calendarTypes.${data.value}.color`)"> {{ data.value }}
          </b-badge>
        </template>


        <template #cell(start)="data">
          <span style="white-space: nowrap"> {{ data.value | moment($t('timeFormat.dateTime.format')) }}</span>
        </template>
      </b-table>

      <b-modal v-model="showModal" title="New Event" no-close-on-backdrop hide-footer>
        <b-form @submit.prevent="save">

          <!-- Time field -->
          <b-form-group label="Time">
            <date-time-picker v-model="newEvent.start"/>
          </b-form-group>

          <!-- Type field -->
          <b-form-group label="Type">
            <v-select
                v-model="newEvent.type"
                :options="types"
                :clearable="false"
                label="label"
                :reduce="(user) => user.name"
            ></v-select>
          </b-form-group>

          <!-- Duration field -->
          <b-form-group label="Duration">
            <v-select
                v-model="newEvent.duration"
                :options="durationOptions"
                :clearable="false"
                label="text"
                :reduce="(user) => user.value"
            ></v-select>
          </b-form-group>

          <!-- Comment field -->
          <b-form-group label="Comment">
            <b-form-textarea v-model="newEvent.body" rows="4" placeholder="Add a comment..."></b-form-textarea>
          </b-form-group>

          <b-button style="float: right" type="submit" variant="primary">{{ $t('operations.save') }}</b-button>
        </b-form>
      </b-modal>

      <!-- === Edit Event Modal === -->
      <b-modal
          v-model="showEditModal"
          title="Edit Event"
          no-close-on-backdrop
          hide-footer
      >
        <b-form @submit.prevent="saveEdit">
          <!-- Time -->
          <b-form-group label="Time">
            <date-time-picker v-model="editEvent.start"/>
          </b-form-group>

          <!-- Type -->
          <b-form-group label="Type">
            <v-select
                v-model="editEvent.type"
                :options="types"
                :clearable="false"
                label="label"
                :reduce="(user) => user.name"
            ></v-select>
          </b-form-group>

          <!-- Duration -->
          <b-form-group label="Duration">
            <v-select
                v-model="editEvent.duration"
                :options="durationOptions"
                :clearable="false"
                label="text"
                :reduce="(user) => user.value"
            ></v-select>
          </b-form-group>

          <!-- Comment -->
          <b-form-group label="Comment">
            <b-form-textarea
                v-model="editEvent.body"
                rows="4"
                placeholder="Edit comment..."
            ></b-form-textarea>
          </b-form-group>

          <b-button
              style="float: right"
              type="submit"
              variant="primary"
          >
            {{ $t('operations.save') }}
          </b-button>
        </b-form>
      </b-modal>

    </div>
    <div v-else-if="error !== ''">
      <b-alert show variant="danger"><i class="fas fa-exclamation-triangle mr-3"></i>
        <strong>{{ error }}</strong>
      </b-alert>
    </div>
    <div v-else>
      <div style="text-align: center;height: 100%;padding: 30% 20px 20px;">
        <b-spinner label="Spinning"/>
        {{ $t('operations.loading') }}
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import mixinValidation from '@/components/mixin/mixinValidation';
import DateTimePicker from '@/components/widget/picker/dateTimePicker.vue'

export default {
  name: 'ClientEvents',
  components: { DateTimePicker },
  props: {
    autoload: {
      default: false,
      type: Boolean
    },
    clientId: String,
  },
  data() {
    return {
      loaded: false,
      loading: false,
      durationOptions: [
        {
          value: 5,
          text: '5 minutes'
        },
        {
          value: 10,
          text: '10 minutes'
        },
        {
          value: 15,
          text: '15 minutes'
        },
        {
          value: 30,
          text: '30 minutes'
        },
        {
          value: 45,
          text: '45 minutes'
        },
        {
          value: 60,
          text: '1 hour'
        },
        {
          value: 120,
          text: '2 hours'
        },
        {
          value: 180,
          text: '3 hours'
        },
        {
          value: 360,
          text: '6 hours'
        }
      ],
      error: '',
      showModal: false,
      showEditModal: false,
      editEvent: {
        id: null,
        time: { date: null, time: null },
        type: null,
        duration: null,
        body: '',
      },
      newEvent: this.defaultNewEvent(),
      events: [],
      fields: [
        {
          key: 'type',
          label: 'Event'
        },

        {
          key: 'start',
          label: 'Time'
        },
        {
          key: 'duration',
          label: 'Duration'
        },
        {
          key: 'body',
          label: 'Comment'
        },

        {
          key: 'actions',
          label: 'Actions'
        },
      ],
    };
  },
  watch: {
    clientId() {
      this.reset();
      if (this.autoload) {
        this.loadData();
      }
    }
  },
  created() {
    this.loadData();
  },
  mixins: [mixinBase, mixinNotifications, mixinPermissions, mixinValidation],

  computed: {
    types() {
      let types = this.$t('calendarTypes');
      return Object.keys(types)
          .map((key) => {
            return {
              name: key,
              label: this.$t(`calendarTypes.${key}.name`)
            };
          });
    },
  },
  methods: {
    ...mapActions('calendar', ['addCalendar', 'cancelEvent', 'updateCalendar','getEventsByClient']),

    onEditEvent(item) {
      console.log('Editing item:', item)

      // Parse from "start" (can be an ISO string like "2025-10-23T14:00:00")
      const datePart = this.extractDate(item.start)
      const timePart = this.extractTime(item.start)

      this.editEvent = {
        id: item.id,
        start: item.start,
        type: item.type || null,
        duration: item.duration || null,
        body: item.body || '',
      }

      this.showEditModal = true
    },

    extractDate(datetime) {
      // helper if your table stores datetime string like "2025-10-23T14:00"
      if (!datetime) return null
      return datetime.split('T')[0]
    },

    extractTime(datetime) {
      if (!datetime) return null
      const date = new Date(datetime)
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}` // local user time
    },
    saveEdit() {
      try {

        this.editEvent.end = this.$moment(this.editEvent.start)
            .add(this.editEvent.duration, 'minutes')
            .toServerDateTimeFormat();

        this.updateCalendar({
          calendar: this.editEvent,
          id: this.editEvent.id,
        })
            .then(() => {
              this.loadData();
              this.showSuccessNotification(
                  this.$t('globalMessages.updateDataSuccess', { system: this.$t('event') })
              );
              this.showEditModal = false;
            })
            .catch((e) => {
              this.error = e.toString();
              this.showErrorNotification(
                  this.$t('globalMessages.updateDataError', { system: this.$t('event') }),
                  'Error updating event'
              );
            });

      } catch (err) {
        console.error('Error saving event:', err);
        this.showErrorNotification('Unexpected error while saving event');
      }
    }
,
    formatDuration(minutes) {
      if (minutes < 60) {
        return `${minutes} minutes`;
      } else {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return `${hours} hour${hours > 1 ? 's' : ''}${remainingMinutes > 0 ? ` ${remainingMinutes} minutes` : ''}`;
      }
    },
    defaultNewEvent() {
      return {
        clientIds: [],
        start: null,
        duration: null,
        type: 'CALL',
        body: '',
      };
    },
    openModal() {
      this.newEvent = this.defaultNewEvent();
      this.showModal = true;
    },
    reset() {
      this.loaded = false;
      this.error = '';
      this.newEvent = this.defaultNewEvent();
      this.events = [];
    },
    loadData() {
      if (this.loading) return;
      this.loading = true;
      this.getEventsByClient(this.clientId)
          .then(eventData => {
            this.events = eventData;
            this.loaded = true;
          })
          .catch(e => {
            this.error = e.toString();
          })
          .finally(() => {
            this.loading = false;
          });
    },
    loadEvent(id) {
      this.get({ id })
          .then(event => {
            this.newEvent = event;
            this.showModal = true;
          });
    },
    onDeleteEvent(id) {
      this.cancelEvent(id)
          .then(() => {
            this.loadData();
            this.showSuccessNotification(this.$t('globalMessages.deleteDataSuccess', { system: this.$t('event') }));

          })
          .catch(e => {
            this.error = e.toString();
            this.showErrorNotification(this.$t('globalMessages.deleteDataError', { system: this.$t('event') }), 'Error deleting event');

          });
    },
    save() {
      let newEvent = {...this.newEvent};
      newEvent.end = this.$moment(newEvent.start)
          .add(newEvent.duration, 'minutes')
          .toServerDateTimeFormat();
      newEvent.clientsIds = [this.clientId];
      this.addCalendar(newEvent)
          .then(() => {
            this.reset();
            this.loadData();
            this.showModal = false;
            this.showSuccessNotification(this.$t('globalMessages.createDataSuccess', { system: this.$t('event') }));

          })
          .catch(e => {
            this.error = e.toString();
            this.showErrorNotification(this.$t('globalMessages.createDataError', { system: this.$t('event') }), 'Error deleting event');

          });
    },
  }
};
</script>

<style>
#client-events {
  min-height: 200px;
}

.fixed-width-cell {
  width: 100vh; /* Adjust this value according to your desired width */
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis; /* Optional: This truncates the text with an ellipsis if it overflows */
}
</style>
